import type { InitialUtm } from '@/api/core-api/types.gen';
import type { UtmSearch, UtmSearchKey } from '@/schemas/utm';

import { pickUtmValues, toInitialUtm } from '@/schemas/utm';

import { deleteCookie, getCookie, setBrowserCookie } from './cookies';

// 5 days
const MAX_AGE_SECONDS = 5 * 24 * 60 * 60;

function cookieName(projectId: string): string {
  return `respondent.projectUtm.${projectId}`;
}

/**
 * Store URL UTM params under a per-project cookie so a later screener
 * submission — including by an already-logged-in user — can be attributed
 * to the campaign that drove them to this project. No-op if no UTMs in URL.
 */
export function persistProjectUtmAttribution(
  projectId: string,
  search: UtmSearch | undefined,
): void {
  const utm = pickUtmValues(search);
  if (Object.keys(utm).length === 0) return;
  setBrowserCookie(cookieName(projectId), JSON.stringify(utm), {
    maxAgeSeconds: MAX_AGE_SECONDS,
  });
}

/**
 * The project-scoped first-touch attribution, in the shape core-api takes.
 * Undefined when this project has none — a corrupt or hand-edited cookie
 * reads as none rather than as a campaign, since `toInitialUtm` keeps only
 * strings under known keys.
 */
export function readProjectInitialUtm(
  projectId: string,
): InitialUtm | undefined {
  const raw = getCookie(cookieName(projectId));
  if (!raw) return undefined;

  let parsed: Partial<Record<UtmSearchKey, unknown>>;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }

  if (!parsed || typeof parsed !== 'object') return undefined;

  return toInitialUtm(parsed);
}

export function clearProjectUtmAttribution(projectId: string): void {
  deleteCookie(cookieName(projectId));
}
